import { AlertCircle, CheckCircle2, RefreshCw, Sparkles } from "lucide-react";
import { Tonal } from "@/components/ui/tonal";
import { Button } from "@/components/ui/button";
import type { FindingKind } from "./workspace-types";

export interface ZeroFindingsStateProps {
  status: string;
  personaName?: string | null;
}

export function ZeroFindingsState({
  status,
  personaName,
}: ZeroFindingsStateProps) {
  if (status === "failed") {
    return (
      <Tonal
        padding="lg"
        className="text-center rounded-lg border border-border/80 space-y-3"
      >
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertCircle className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="space-y-1 max-w-[48ch] mx-auto">
          <h3 className="text-base font-semibold text-foreground">
            No findings recorded
          </h3>
          <p className="text-sm text-muted-foreground">
            This walkthrough encountered an error and could not complete. No
            findings or observations were recorded.
          </p>
        </div>
      </Tonal>
    );
  }

  if (status === "cancelled") {
    return (
      <Tonal
        padding="lg"
        className="text-center rounded-lg border border-border/80 space-y-3"
      >
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <AlertCircle className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="space-y-1 max-w-[48ch] mx-auto">
          <h3 className="text-base font-semibold text-foreground">
            Walkthrough cancelled
          </h3>
          <p className="text-sm text-muted-foreground">
            This walkthrough was cancelled before completion. No findings were
            recorded.
          </p>
        </div>
      </Tonal>
    );
  }

  if (status === "running" || status === "pending") {
    return (
      <Tonal
        padding="lg"
        className="text-center rounded-lg border border-border/80 space-y-3"
      >
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <RefreshCw className="h-5 w-5 animate-spin" aria-hidden="true" />
        </div>
        <div className="space-y-1 max-w-[48ch] mx-auto">
          <h3 className="text-base font-semibold text-foreground">
            Waiting for findings…
          </h3>
          <p className="text-sm text-muted-foreground">
            Findings and observations will appear here as{" "}
            {personaName || "the persona"} navigates the scenario.
          </p>
        </div>
      </Tonal>
    );
  }

  return (
    <Tonal
      padding="lg"
      className="text-center rounded-lg border border-border/80 space-y-4"
    >
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-finding-helps">
        <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
      </div>
      <div className="space-y-1.5 max-w-[48ch] mx-auto">
        <h3 className="text-base font-semibold text-foreground">
          No problems found
        </h3>
        <p className="text-sm text-muted-foreground">
          {personaName || "Persona"} walked through this scenario without
          encountering any problems.
        </p>
      </div>
    </Tonal>
  );
}

export interface FilteredFindingsEmptyStateProps {
  isComplete: boolean;
  problemsCount: number;
  helpsCount: number;
  openCount: number;
  kindFilter: FindingKind;
  statusFilter: string;
  personaName?: string | null;
  onClearFilters: () => void;
  onViewStrengths: () => void;
  onViewAllAddressed: () => void;
}

export function FilteredFindingsEmptyState({
  isComplete,
  problemsCount,
  helpsCount,
  openCount,
  kindFilter,
  statusFilter,
  personaName,
  onClearFilters,
  onViewStrengths,
  onViewAllAddressed,
}: FilteredFindingsEmptyStateProps) {
  if (isComplete && problemsCount === 0 && kindFilter === "problem") {
    return (
      <Tonal
        padding="lg"
        className="text-center rounded-lg border border-border/80 space-y-4"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-finding-helps">
          <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="space-y-1.5 max-w-[48ch] mx-auto">
          <h3 className="text-base font-semibold text-foreground">
            No problems found
          </h3>
          <p className="text-sm text-muted-foreground">
            {personaName || "Persona"} walked through this scenario without
            encountering any problems.
          </p>
        </div>
        {helpsCount > 0 && (
          <div className="mx-auto mt-4 max-w-md rounded-lg border border-border/80 bg-muted/30 p-4 text-left flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <Sparkles
                className="h-4 w-4 text-finding-helps shrink-0"
                aria-hidden="true"
              />
              <span className="text-sm font-medium text-foreground">
                Surfaced {helpsCount}{" "}
                {helpsCount === 1
                  ? "positive signal / strength"
                  : "positive signals / strengths"}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={onViewStrengths}
            >
              View strengths
            </Button>
          </div>
        )}
      </Tonal>
    );
  }

  if (kindFilter === "strength" && helpsCount === 0) {
    return (
      <Tonal
        padding="lg"
        className="text-center rounded-lg border border-border/80 space-y-4"
      >
        <div className="space-y-1.5 max-w-[48ch] mx-auto">
          <h3 className="text-base font-semibold text-foreground">
            No strengths observed
          </h3>
          <p className="text-sm text-muted-foreground">
            {personaName || "Persona"} did not record any positive observations
            in this walkthrough.
          </p>
        </div>
      </Tonal>
    );
  }

  if (statusFilter === "open" && openCount === 0 && problemsCount > 0) {
    return (
      <Tonal
        padding="lg"
        className="text-center rounded-lg border border-border/80 space-y-4"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="space-y-1.5 max-w-[48ch] mx-auto">
          <h3 className="text-base font-semibold text-foreground">
            All problems addressed
          </h3>
          <p className="text-sm text-muted-foreground">
            All {problemsCount} problems identified in this walkthrough have
            been resolved or triaged.
          </p>
        </div>
        <div className="pt-1">
          <Button size="sm" variant="outline" onClick={onViewAllAddressed}>
            View addressed problems
          </Button>
        </div>
      </Tonal>
    );
  }

  return (
    <Tonal
      padding="md"
      className="text-center rounded-lg border border-border/80 space-y-3"
    >
      <p className="text-sm text-muted-foreground">
        No findings match the active filters.
      </p>
      <div>
        <Button size="sm" variant="outline" onClick={onClearFilters}>
          Clear filters
        </Button>
      </div>
    </Tonal>
  );
}
